import React, { useState, useContext, useRef, useEffect, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Upload, FileText, X, AlertCircle, WifiOff } from "lucide-react";
import { mixpanel } from "../../../../utils/mixpanel";
import {
  beginPatientAudioUpload,
  startAudioProcessing,
  uploadAudio,
} from "../../../../services/api-documentation";
import MP3EncoderService from "../../../../services/mp3encoder";
import { SessionContext } from "../../../../contexts";
import { logUIError } from "../../../../services/api-misc";

// Encounter type shared components
import EncounterTypeModal from "./encounter-type/EncounterTypeModal";
import { useEncounterTypes } from "./encounter-type/useEncounterTypes";

const getTodayLocalISODate = (): string => {
  const now = new Date();
  const tzOffsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 10);
};

const normalizeNoteDate = (value?: string | null): string | null => {
  if (!value) return null;

  const trimmedValue = value.trim();
  const isoDateMatch = trimmedValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDateMatch) {
    return isoDateMatch[1];
  }

  const parsedDate = new Date(trimmedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  const tzOffsetMs = parsedDate.getTimezoneOffset() * 60000;
  return new Date(parsedDate.getTime() - tzOffsetMs).toISOString().slice(0, 10);
};

const getDefaultNoteDate = (consultationDate?: string | null): string => {
  const today = getTodayLocalISODate();
  const appointmentDate = normalizeNoteDate(consultationDate);

  if (!appointmentDate) {
    return today;
  }

  return appointmentDate <= today ? appointmentDate : today;
};

const TEN_MINUTE_WARNING_BYTES = 10.4 * 1024 * 1024; // ~10 minutes of audio
const ONE_MB_BYTES = 1 * 1024 * 1024;

const formatFileSize = (bytes: number): string => {
  if (!bytes) return "0 KB";
  if (bytes >= ONE_MB_BYTES) {
    return `${(bytes / ONE_MB_BYTES).toFixed(1)} MB`;
  }
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const formatDurationLabel = (seconds: number): string => {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${minutes}:${String(secs).padStart(2, "0")}`;
};

const readAudioDurationSeconds = (file: File): Promise<number | null> =>
  new Promise((resolve) => {
    const audio = document.createElement("audio");
    const objectUrl = URL.createObjectURL(file);
    let settled = false;

    const cleanup = () => {
      if (!settled) settled = true;
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("error", onError);
      URL.revokeObjectURL(objectUrl);
      audio.src = "";
    };

    const onLoadedMetadata = () => {
      const duration = audio.duration;
      cleanup();
      if (Number.isFinite(duration) && duration > 0) {
        resolve(duration);
      } else {
        resolve(null);
      }
    };

    const onError = () => {
      cleanup();
      resolve(null);
    };

    audio.preload = "metadata";
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("error", onError);
    audio.src = objectUrl;
  });

interface AudioUploaderProps {
  patient: {
    id: string;
    name: string;
    patientRecordId?: string;
    email?: string;
    clinicOSUUID?: string;
    appointmentId?: number | null; // attaches note to appointment
    consultationDate?: string | null;
  };
  onUploadComplete: (result: {
    transcription: string;
    summary: string;
    audioUrl: string;
    audioId: number;
    noteType: string;
  }) => void;
}

type NoteType = string | null;

const AudioUploader: React.FC<AudioUploaderProps> = ({
  patient,
  onUploadComplete,
}) => {
  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.session;

  const logAudioUIError = async (
    message: string,
    extraContext?: Record<string, unknown>,
  ) => {
    try {
      await logUIError({
        message,
        page_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        user_email: session?.userEmail || undefined,
        clinic_name: session?.clinicName || undefined,
        clinic_id: session?.clinicId || undefined,
        context: {
          component: "AudioUploader",
          patientId: patient?.clinicOSUUID,
          appointmentId: patient?.appointmentId,
          ...extraContext,
        },
      });
    } catch (err) {
      console.warn("logAudioUIError failed", err);
    }
  };

  // State
  const [isUploading, setIsUploading] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [optimizedBlob, setOptimizedBlob] = useState<Blob | null>(null);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [uiError, setUiError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [optimizationProgress, setOptimizationProgress] = useState(0);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState("");
  const defaultNoteDate = useMemo(
    () => getDefaultNoteDate(patient.consultationDate),
    [patient.consultationDate],
  );
  const [noteDate, setNoteDate] = useState<string>(defaultNoteDate);
  const [fileSizeWarning, setFileSizeWarning] = useState<
    "small" | "tiny" | null
  >(null);
  const [sizeWarningAcknowledged, setSizeWarningAcknowledged] = useState(true);
  const [warningDurationSeconds, setWarningDurationSeconds] = useState<
    number | null
  >(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setNoteDate(defaultNoteDate);
  }, [defaultNoteDate, patient.appointmentId, patient.clinicOSUUID]);

  // Encounter types (shared hook)
  const {
    available,
    loading: typesLoading,
    error: typesError,
    createType,
  } = useEncounterTypes(session);
  const maxNoteDate = useMemo(() => getTodayLocalISODate(), []);
  const uploadSizeLabel = optimizedBlob
    ? formatFileSize(optimizedBlob.size)
    : null;
  const warningDurationLabel =
    warningDurationSeconds != null
      ? formatDurationLabel(warningDurationSeconds)
      : null;
  const sizeWarningCopy =
    fileSizeWarning === "tiny"
      ? warningDurationLabel
        ? `Detected duration: ${warningDurationLabel}. Please double-check that the entire recording was captured.`
        : "This file is extremely small (<1 MB). Please double-check that the entire recording was captured."
      : fileSizeWarning === "small"
        ? warningDurationLabel
          ? `Detected duration: ${warningDurationLabel}. This seems short for a typical 10-minute recording. Are you sure this is the full audio file?`
          : "This file size is much smaller than a typical 10-minute recording. Are you sure this is the full audio file?"
        : null;

  const updateFileSizeWarning = (blob: Blob | null) => {
    if (!blob) {
      setFileSizeWarning(null);
      setSizeWarningAcknowledged(true);
      setWarningDurationSeconds(null);
      return;
    }

    if (blob.size < ONE_MB_BYTES) {
      setFileSizeWarning("tiny");
      setSizeWarningAcknowledged(false);
      setWarningDurationSeconds(null);
      return;
    }

    if (blob.size < TEN_MINUTE_WARNING_BYTES) {
      setFileSizeWarning("small");
      setSizeWarningAcknowledged(false);
      setWarningDurationSeconds(null);
      return;
    }

    setFileSizeWarning(null);
    setSizeWarningAcknowledged(true);
    setWarningDurationSeconds(null);
  };

  const updateWarningWithDuration = async (file: File, blob: Blob | null) => {
    const durationSeconds = await readAudioDurationSeconds(file);
    if (durationSeconds == null) {
      console.info(
        "Audio duration unavailable; falling back to size warning.",
        {
          fileType: file.type,
          fileSize: file.size,
        },
      );
      updateFileSizeWarning(blob);
      return;
    }

    setWarningDurationSeconds(durationSeconds);

    if (durationSeconds < 120) {
      setFileSizeWarning("tiny");
      setSizeWarningAcknowledged(false);
      return;
    }

    if (durationSeconds < 420) {
      setFileSizeWarning("small");
      setSizeWarningAcknowledged(false);
      return;
    }

    setFileSizeWarning(null);
    setSizeWarningAcknowledged(true);
  };

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const processFile = async (file: File) => {
    setUiError(null);

    // Validate file type
    const isAudio =
      file.type.startsWith("audio/") ||
      file.type === "audio/mp4" ||
      file.type === "audio/x-m4a";

    if (!isAudio) {
      setUiError("Please select an audio file (MP3, WAV, M4A, etc.)");
      return;
    }

    // Check size (<= 200MB)
    if (file.size > 200 * 1024 * 1024) {
      setUiError(
        "File size exceeds 200MB limit. Please use a compressed audio format like MP3 or M4A.",
      );
      return;
    }

    // Feature support
    if (!MP3EncoderService.isSupported()) {
      setUiError(
        "Your browser doesn't support the required audio features for optimization.",
      );
      return;
    }

    // Ensure the patient is linked to a ClinicOS record before uploading.
    setIsUploading(true);
    try {
      ensurePatientLinked();
    } catch {
      setIsUploading(false);
      return;
    }

    // Optimize/convert
    setIsUploading(false);
    setIsOptimizing(true);
    setProcessingStep("Processing audio file...");
    setOptimizationProgress(0);

    try {
      const audioBlob = new Blob([file], { type: file.type });

      const validation = MP3EncoderService.validateAudioBlob(audioBlob);
      if (!validation.isValid) {
        throw new Error(validation.error || "Invalid audio file");
      }

      setOptimizationProgress(25);

      const processedBlob = await MP3EncoderService.convertToMP3(audioBlob, {
        sampleRate: 44100,
        bitRate: 128,
        channels: 1,
      });

      setOptimizationProgress(100);

      mixpanel.track("Audio File Processing Success", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Original Size (bytes)": file.size,
        "Processed Size (bytes)": processedBlob.size,
        "Size Ratio": Math.round((processedBlob.size / file.size) * 100),
        "Original Format": file.type,
        "Processed Format": processedBlob.type,
      });

      setOptimizedBlob(processedBlob);
      await updateWarningWithDuration(file, processedBlob);
      setUploadedFile(file);
    } catch (processingError: any) {
      console.warn(
        "Audio processing failed, using original file:",
        processingError?.message,
      );

      mixpanel.track("Audio File Processing Failed", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "File Size": file.size,
        "File Type": file.type,
        Error: processingError?.message || "Unknown error",
      });
      logAudioUIError("Audio file processing failed; using original file", {
        fileSize: file.size,
        fileType: file.type,
        error:
          processingError instanceof Error
            ? processingError.message
            : String(processingError),
      });

      const originalBlob = new Blob([file], { type: file.type });
      setOptimizedBlob(originalBlob);
      await updateWarningWithDuration(file, originalBlob);
      setUploadedFile(file);
    } finally {
      setIsOptimizing(false);
      setProcessingStep("");
      setShowTypeModal(true);
    }

    mixpanel.track("Audio File Selected", {
      "User Email": session?.userEmail,
      "Clinic Name": session?.clinicName,
      "Patient ID": patient.clinicOSUUID,
      "Patient Name": patient.name,
      "File Type": file.type,
      "File Size": file.size,
      "Upload Method": "File Selection",
    });
  };

  const ensurePatientLinked = (): void => {
    if (patient.clinicOSUUID) return;

    // No ClinicOS UUID means this patient isn't linked to a ClinicOS record.
    // Block the upload and surface it rather than trying to resolve it.
    setUiError("This patient isn't linked to a ClinicOS record.");
    throw new Error("Patient not linked to a ClinicOS record");
  };

  // Drag & drop
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const { clientX: x, clientY: y } = e;
    if (
      x <= rect.left ||
      x >= rect.right ||
      y <= rect.top ||
      y >= rect.bottom
    ) {
      setIsDragging(false);
    }
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      processFile(file);
      mixpanel.track("Audio File Dropped", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "File Type": file.type,
        "File Size": file.size,
        "Upload Method": "Drag and Drop",
      });
    }
  };

  const handleProcessFile = async (
    noteType: NoteType,
    selectedNoteDate: string,
  ) => {
    if (!noteType || !optimizedBlob || !patient.clinicOSUUID) {
      setUiError(
        "Missing required data. Please ensure patient is synced and file is selected.",
      );
      return;
    }

    setShowTypeModal(false);
    setIsProcessing(true);

    if (!session) {
      setUiError("Please log in to upload audio for this patient.");
      setIsProcessing(false);
      return;
    }

    try {
      // Phase 1 — Upload
      setProcessingStep("Uploading audio...");
      const { audioId, putUrl, getUrl } = await beginPatientAudioUpload(
        session,
        patient.clinicOSUUID!,
        "surgeon_audio",
        selectedNoteDate || undefined,
        { source: "AudioUploader.tsx" },
      );
      await uploadAudio(optimizedBlob!, putUrl, {
        session,
        patientId: patient.clinicOSUUID,
        audioId,
        source: "AudioUploader.tsx",
      });

      // Phase 2 — Process
      setProcessingStep("Processing… you can come back later.");
      const { summary, transcription } = await startAudioProcessing(
        session,
        audioId,
        patient.clinicOSUUID!,
        noteType,
        patient.appointmentId,
        selectedNoteDate || undefined,
      );

      mixpanel.track("Audio Upload Success", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note Type": noteType,
        "File Size": uploadedFile?.size || 0,
        "Optimized Size": optimizedBlob.size,
        "File Type": uploadedFile?.type || "unknown",
        "Audio Format": "Optimized",
      });

      onUploadComplete({
        transcription,
        summary,
        audioUrl: getUrl,
        audioId,
        noteType,
      });

      // Reset
      setIsProcessing(false);
      setUploadedFile(null);
      setOptimizedBlob(null);
      setProcessingStep("");
      updateFileSizeWarning(null);
    } catch (err: any) {
      console.error("Audio processing failed:", err);
      setUiError("We couldn't process this audio file. Please try again.");

      mixpanel.track("Audio Upload Processing Error", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note Type": noteType,
        "Error Message": err instanceof Error ? err.message : "Unknown error",
        "File Size": uploadedFile?.size || 0,
      });

      logAudioUIError("Audio upload or processing failed", {
        noteType,
        fileSize: uploadedFile?.size,
        patientClinicOSUUID: patient.clinicOSUUID,
        appointmentId: patient.appointmentId,
        error: err instanceof Error ? err.message : String(err),
        processingStep,
      });

      setIsProcessing(false);
      setProcessingStep("");
    }
  };

  const cancelUpload = () => {
    setUploadedFile(null);
    setOptimizedBlob(null);
    setUiError(null);
    setProcessingStep("");
    setOptimizationProgress(0);
    setNoteDate(getTodayLocalISODate());
    updateFileSizeWarning(null);

    if (uploadedFile) {
      mixpanel.track("Audio Upload Cancelled", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "File Size": uploadedFile.size,
      });
    }
  };

  const handleWarningAccept = () => {
    setSizeWarningAcknowledged(true);
  };

  const handleWarningCancel = () => {
    setShowTypeModal(false);
    cancelUpload();
  };

  const typeModalWarningConfig =
    fileSizeWarning && optimizedBlob && sizeWarningCopy
      ? {
          level: fileSizeWarning,
          message: sizeWarningCopy,
          sizeLabel: warningDurationLabel
            ? `Duration: ${warningDurationLabel}`
            : uploadSizeLabel
              ? `File size: ${uploadSizeLabel}`
              : "File size unavailable",
          onAccept: handleWarningAccept,
          onCancel: handleWarningCancel,
        }
      : undefined;

  return (
    <View style={styles.container}>
      {/* Online/Offline indicator */}
      {!isOnline && (
        <View style={styles.offlineIndicator}>
          <WifiOff size={16} color="#E53935" />
          <Text style={styles.offlineText}>
            Offline - uploads will be queued and processed when connection is
            restored
          </Text>
        </View>
      )}

      {/* Error display */}
      {uiError && (
        <View style={styles.errorContainer}>
          <AlertCircle size={20} color="#E53935" />
          <Text style={styles.errorText}>{uiError}</Text>
        </View>
      )}
      {/* Hidden file input (web) */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="audio/*,.m4a"
        style={{ display: "none" }}
      />

      {isProcessing ? (
        <View style={styles.processingContainer}>
          <ActivityIndicator size="small" color="#0085FF" />
          <Text style={styles.processingText}>{processingStep}</Text>
        </View>
      ) : isOptimizing ? (
        <View style={styles.optimizingContainer}>
          <ActivityIndicator size="small" color="#4CAF50" />
          <View style={styles.optimizingContent}>
            <Text style={styles.optimizingText}>{processingStep}</Text>
            <View style={styles.progressContainer}>
              <View
                style={[
                  styles.progressBar,
                  { width: `${optimizationProgress}%` },
                ]}
              />
            </View>
          </View>
        </View>
      ) : uploadedFile ? (
        <View style={styles.fileContainer}>
          <FileText size={20} color="#4CAF50" />
          <View style={styles.fileInfo}>
            <Text style={styles.fileName}>{uploadedFile.name}</Text>
            <View style={styles.fileSizeContainer}>
              <Text style={styles.fileSize}>
                Original: {formatFileSize(uploadedFile.size)}
              </Text>
              {optimizedBlob && optimizedBlob.size !== uploadedFile.size && (
                <Text style={styles.optimizedSize}>
                  Upload Size: {formatFileSize(optimizedBlob.size)} (
                  {Math.round(
                    (1 - optimizedBlob.size / uploadedFile.size) * 100,
                  )}
                  % smaller)
                </Text>
              )}
            </View>
            {optimizedBlob && (
              <Text style={styles.sizeHint}>
                Final upload: {formatFileSize(optimizedBlob.size)}
              </Text>
            )}
          </View>
          {fileSizeWarning && sizeWarningCopy && (
            <View
              style={[
                styles.fileSizeWarning,
                fileSizeWarning === "tiny"
                  ? styles.fileSizeWarningSevere
                  : styles.fileSizeWarningMild,
              ]}
            >
              <AlertCircle
                size={18}
                color={fileSizeWarning === "tiny" ? "#C62828" : "#F57C00"}
              />
              <Text style={styles.fileSizeWarningText}>
                {sizeWarningCopy}
                {uploadSizeLabel ? ` Current size: ${uploadSizeLabel}.` : ""}
              </Text>
            </View>
          )}
          <TouchableOpacity style={styles.cancelButton} onPress={cancelUpload}>
            <X size={16} color="#555555" />
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.dragWrapper}>
          <div
            style={{ display: "inline-block", cursor: "pointer" }}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <TouchableOpacity
              style={[
                styles.uploadButton,
                isDragging && styles.uploadButtonDragging,
              ]}
              onPress={handleUploadClick}
              disabled={isUploading}
            >
              {isUploading ? (
                <ActivityIndicator size="small" color="#000000" />
              ) : (
                <>
                  <Upload size={20} color="#000000" />
                  <Text style={styles.uploadButtonText}>
                    {isDragging ? "Drop Audio File Here" : "Upload Audio File"}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </div>
        </View>
      )}

      {/* Encounter Type Modal (shared) */}
      <EncounterTypeModal
        visible={showTypeModal}
        onClose={() => {
          setShowTypeModal(false);
          cancelUpload();
        }}
        onSelect={async (type, selectedDate) => {
          const normalizedType =
            (typeof type === "string" ? type.trim() : "") || null;
          if (!normalizedType) {
            setUiError("Please choose a note type.");
            return;
          }
          const normalizedDate = selectedDate || getTodayLocalISODate();
          setNoteDate(normalizedDate);
          await handleProcessFile(normalizedType, normalizedDate);
        }}
        loading={typesLoading}
        error={typesError || undefined}
        types={available}
        allowCreate
        onCreateType={async (name: string) => {
          try {
            await createType(name);
          } catch (e) {
            // optional: surface error locally if modal doesn't already
            setUiError(
              e instanceof Error ? e.message : "Failed to create type",
            );
          }
        }}
        noteDate={noteDate}
        maxDate={maxNoteDate}
        onChangeNoteDate={(value) => setNoteDate(value)}
        disableNoteDate={isUploading || isOptimizing || isProcessing}
        warningConfig={typeModalWarningConfig}
        warningAcknowledged={sizeWarningAcknowledged}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { marginTop: 4, marginBottom: 8 },
  offlineIndicator: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFF3E0",
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  offlineText: { color: "#E65100", fontSize: 12, marginLeft: 8, flex: 1 },
  dragWrapper: { alignItems: "flex-start", width: "auto", display: "flex" },
  errorContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFEBEE",
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
  },
  errorText: { color: "#E53935", fontSize: 14, marginLeft: 8 },
  uploadButton: {
    backgroundColor: "#A1E3CB",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
    alignSelf: "flex-start",
    gap: 10,
  },
  uploadButtonDragging: { backgroundColor: "#8FD8BD" },
  uploadButtonText: {
    color: "#000000",
    fontFamily: "Urbanist500",
    textAlign: "center",
    fontSize: 16,
    lineHeight: 22.4,
    letterSpacing: 0.032,
  },
  optimizingContainer: {
    flexDirection: "row",
    backgroundColor: "#E8F5E9",
    padding: 16,
    borderRadius: 8,
    alignItems: "center",
  },
  optimizingContent: { flex: 1, marginLeft: 12 },
  optimizingText: { color: "#4CAF50", fontSize: 16, fontWeight: "500" },
  progressContainer: {
    height: 4,
    backgroundColor: "#C8E6C9",
    borderRadius: 2,
    marginTop: 8,
    overflow: "hidden",
  },
  progressBar: { height: "100%", backgroundColor: "#4CAF50", borderRadius: 2 },
  processingContainer: {
    flexDirection: "row",
    backgroundColor: "#E3F2FD",
    padding: 16,
    borderRadius: 8,
    alignItems: "center",
  },
  processingText: {
    color: "#0085FF",
    fontSize: 16,
    fontWeight: "500",
    marginLeft: 12,
  },
  fileContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F5F5F5",
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E8F5E9",
  },
  fileInfo: { flex: 1, marginLeft: 10 },
  fileName: { fontSize: 14, fontWeight: "500", color: "#333333" },
  fileSizeContainer: { marginTop: 2 },
  fileSize: { fontSize: 12, color: "#666666" },
  optimizedSize: { fontSize: 12, color: "#4CAF50", fontWeight: "500" },
  sizeHint: { fontSize: 11, color: "#757575", marginTop: 4 },
  fileSizeWarning: {
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 10,
    borderRadius: 6,
    marginTop: 10,
  },
  fileSizeWarningMild: { backgroundColor: "#FFF8E1", borderColor: "#FFE0B2" },
  fileSizeWarningSevere: {
    backgroundColor: "#FFEBEE",
    borderWidth: 1,
    borderColor: "#EF5350",
  },
  fileSizeWarningText: {
    flex: 1,
    fontSize: 12,
    color: "#5D4037",
    marginLeft: 8,
  },
  cancelButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "#EEEEEE",
    justifyContent: "center",
    alignItems: "center",
  },
});

export default AudioUploader;
